/**
 * Estado do aparelho, no cofre.
 *
 * Tudo o que persiste passa por `sealVault`: identidade, pre-chaves e o
 * estado do ratchet de cada contato. O que vai para o `localStorage` e um
 * blob opaco; a chave sai da frase e nunca e guardada.
 *
 * Isto e o substituto possivel, no navegador, do SecureStore mais SQLCipher
 * que docs/01 prevê no celular. Nao e equivalente: nao ha enclave, e
 * enquanto a aba esta aberta o estado esta decifrado em memoria. A UI diz
 * isso em voz alta em vez de deixar a pessoa supor o contrario.
 */
import {
  ContactTrust,
  Identity,
  PreKeyStore,
  RatchetSession,
  deriveSafetyCode,
  fingerprintFromText,
  fromBase64Url,
  openVault,
  sealVault,
  toBase64Url,
  type SerializedPreKeyStore,
  type SerializedSession,
  type Session,
  type X3dhInitialMessage,
} from '@criptohomer/crypto-core'

const CHAVE_LOCAL = 'criptohomer.cofre.v1'

export interface Mensagem {
  readonly minha: boolean
  readonly texto: string
  readonly em: number
}

export interface ContatoPersistido {
  readonly apelido: string
  readonly sessao: SerializedSession
  readonly associatedData: string
  readonly confianca: ContactTrust
  readonly conversa: readonly Mensagem[]
  /**
   * Material do X3DH que ainda precisa acompanhar a PRIMEIRA mensagem.
   *
   * Persiste porque a pessoa pode criar a sessao hoje e so escrever amanha,
   * com o app fechado no meio. Sem isso, o convite se perderia e o outro
   * lado receberia um quadro que nao tem como abrir — a sessao nasceria
   * morta e sem explicacao. Ausente = convite ja enviado.
   */
  readonly convitePendente?: {
    readonly initiatorSignPublicKey: string
    readonly initiatorAgreePublicKey: string
    readonly ephemeralPublicKey: string
    readonly signedPreKeyId: number
    readonly oneTimePreKeyId?: number
  }
}

interface EstadoPersistido {
  readonly v: 1
  readonly identity: {
    readonly signPublicKey: string
    readonly signPrivateKey: string
    readonly agreePublicKey: string
    readonly agreePrivateKey: string
  }
  readonly preKeys: SerializedPreKeyStore
  readonly contatos: Record<string, ContatoPersistido>
}

/** Contato ja reidratado: o que o resto do app manipula. */
export interface Contato {
  apelido: string
  sessao: Session
  confianca: ContactTrust
  conversa: Mensagem[]
  /** Ausente depois que a primeira mensagem saiu. */
  convitePendente?: X3dhInitialMessage
}

export class Cofre {
  private constructor(
    private frase: Uint8Array,
    readonly identity: Identity,
    readonly preKeys: PreKeyStore,
    readonly contatos: Map<string, Contato>,
  ) {}

  static existe(): boolean {
    return localStorage.getItem(CHAVE_LOCAL) !== null
  }

  /** Primeiro uso: identidade nova, nada no servidor ainda. */
  static criar(frase: Uint8Array, agora: number): Cofre {
    const identity = Identity.generate()
    return new Cofre(frase, identity, PreKeyStore.create(identity, agora), new Map())
  }

  /** Lanca se a frase estiver errada — e nao diz se o problema foi a frase. */
  static abrir(frase: Uint8Array): Cofre {
    const bruto = localStorage.getItem(CHAVE_LOCAL)
    if (bruto === null) throw new Error('nao ha cofre neste navegador')

    const claro = openVault(frase, fromBase64Url(bruto))
    const estado = JSON.parse(new TextDecoder().decode(claro)) as EstadoPersistido

    const identity = Identity.fromKeys(
      fromBase64Url(estado.identity.signPublicKey),
      fromBase64Url(estado.identity.signPrivateKey),
      fromBase64Url(estado.identity.agreePublicKey),
      fromBase64Url(estado.identity.agreePrivateKey),
    )

    const contatos = new Map<string, Contato>()
    for (const [fp, c] of Object.entries(estado.contatos)) {
      contatos.set(fp, {
        apelido: c.apelido,
        confianca: c.confianca,
        conversa: [...c.conversa],
        ...(c.convitePendente === undefined
          ? {}
          : {
              convitePendente: {
                initiatorSignPublicKey: fromBase64Url(c.convitePendente.initiatorSignPublicKey),
                initiatorAgreePublicKey: fromBase64Url(c.convitePendente.initiatorAgreePublicKey),
                ephemeralPublicKey: fromBase64Url(c.convitePendente.ephemeralPublicKey),
                signedPreKeyId: c.convitePendente.signedPreKeyId,
                ...(c.convitePendente.oneTimePreKeyId === undefined
                  ? {}
                  : { oneTimePreKeyId: c.convitePendente.oneTimePreKeyId }),
              },
            }),
        sessao: {
          ratchet: RatchetSession.deserialize(c.sessao, fromBase64Url),
          associatedData: fromBase64Url(c.associatedData),
          // Recalculado, nunca lido do disco: o codigo de 6 palavras tem de
          // sair dos fingerprints de verdade. Um codigo guardado que
          // divergisse das identidades mostraria "verificado" para uma
          // sessao que ja nao e a mesma.
          safetyCode: deriveSafetyCode(identity.fingerprint, fingerprintFromText(fp)),
        },
      })
    }

    return new Cofre(
      frase,
      identity,
      PreKeyStore.deserialize(estado.preKeys, fromBase64Url),
      contatos,
    )
  }

  /** Selar de novo a cada mudanca: o ratchet nao pode voltar no tempo. */
  salvar(): void {
    const contatos: Record<string, ContatoPersistido> = {}
    for (const [fp, c] of this.contatos) {
      contatos[fp] = {
        apelido: c.apelido,
        sessao: c.sessao.ratchet.serialize(),
        associatedData: toBase64Url(c.sessao.associatedData),
        confianca: c.confianca,
        conversa: c.conversa,
        ...(c.convitePendente === undefined
          ? {}
          : {
              convitePendente: {
                initiatorSignPublicKey: toBase64Url(c.convitePendente.initiatorSignPublicKey),
                initiatorAgreePublicKey: toBase64Url(c.convitePendente.initiatorAgreePublicKey),
                ephemeralPublicKey: toBase64Url(c.convitePendente.ephemeralPublicKey),
                signedPreKeyId: c.convitePendente.signedPreKeyId,
                ...(c.convitePendente.oneTimePreKeyId === undefined
                  ? {}
                  : { oneTimePreKeyId: c.convitePendente.oneTimePreKeyId }),
              },
            }),
      }
    }

    const segredos = this.identity.exportSecrets()
    const estado: EstadoPersistido = {
      v: 1,
      identity: {
        signPublicKey: toBase64Url(this.identity.publicIdentity.signPublicKey),
        signPrivateKey: toBase64Url(segredos.signPrivateKey),
        agreePublicKey: toBase64Url(this.identity.publicIdentity.agreePublicKey),
        agreePrivateKey: toBase64Url(segredos.agreePrivateKey),
      },
      preKeys: this.preKeys.serialize(),
      contatos,
    }

    const claro = new TextEncoder().encode(JSON.stringify(estado))
    localStorage.setItem(CHAVE_LOCAL, toBase64Url(sealVault(this.frase, claro)))
  }

  /**
   * Autodestruicao. Apaga o blob e zera as chaves em memoria.
   *
   * Nao promete apagado forense: o navegador pode ter deixado copia em
   * cache, swap ou backup do sistema, e isso esta fora do nosso alcance.
   */
  destruir(): void {
    localStorage.removeItem(CHAVE_LOCAL)
    this.identity.wipe()
    this.contatos.clear()
    this.frase.fill(0)
  }
}
